import React from "react";
import { Path, GNode, Bridge } from "./assets/GFX";
import { applyDir, cellIsFacing, invertDir } from "./CellUtils";
interface CellPropsCommon {
  colorlow: string;
  colorVert: string;
  x: number;
  y: number;
  click?: (p: Readonly<CellProps>) => void;
  selected: boolean;
  key?: React.Key;
}
interface CellSimple extends CellPropsCommon {
  kind: "empty" | "node" | "bridge";
}
interface CellPath extends CellPropsCommon {
  kind: "path";
  directionA: Direction;
  directionB?: Direction;
}
export type CellProps = CellSimple | CellPath;
export type Direction = "up" | "down" | "left" | "right";
export type Pos = [number, number];
export const Cell = React.memo((props: CellProps) => {
  let guts: JSX.Element;
  switch (props.kind) {
    case "empty":
      guts = <svg />;
      break;
    case "path":
      guts = (
        <Path
          A={props.directionA}
          B={props.directionB}
          color={props.colorlow}
        />
      );
      break;
    case "node":
      guts = <GNode color={props.colorlow} />;
      break;
    case "bridge":
      guts = <Bridge horiz={props.colorlow} vert={props.colorVert} />;
      break;
  }
  return (
    <div
      className={`game-cell ${props.selected ? "sel" : ""}`}
      onClick={() => props.click?.(props)}
    >
      {guts}
    </div>
  );
});
export type CellGridProps = {
  cells: CellProps[][];
  x: number;
  y: number;
  logic?: (cells: ReadonlyArray<ReadonlyArray<Readonly<CellProps>>>) => void;
};
type CellGridState = {
  elems: CellProps[][];
  sel: [number, number];
  sel_col: string;
  //to force rerender
  rev: number;
};
export class CellGrid extends React.Component<CellGridProps, CellGridState> {
  constructor(props: CellGridProps) {
    super(props);
    (this.state as CellGridState) = {
      elems: props.cells,
      sel: [0, 0],
      sel_col: "",
      rev: 0,
    };
  }
  render() {
    return (
      <table>
        <tbody>
          {this.state.elems.map((i) => (
            <tr>
              {i.map((j) => (
                <td>
                  <Cell {...j} click={this.onClick} key={`${j.x} ${j.y}`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  readonly onClick = (props: Readonly<CellProps>): void => {
    let [x, y] = [props.x, props.y];
    let [sx, sy] = this.state.sel;
    let sprops = this.state.elems[sy][sx];
    let dir = deltaToDir(sx - x, sy - y);
    if (x === sx && y === sy) {
      //same, reset cell
      this.setState((s) => {
        let t = s.elems[y][x];
        if (t.kind === "path") {
          t.colorlow = "white";
          t.directionA = undefined!;
          t.directionB = undefined;
        }
        if (t.kind === "path" || t.kind === "empty") {
          t.kind = "empty";
        }
        if (t.kind === "bridge") {
          t.colorlow = "white";
          t.colorVert = "white";
        }

        return { ...s, rev: s.rev + 1 };
      });
    }
    if (nextto(x, y, sx, sy) && dir) {
      //next-to
      switch (props.kind) {
        case "bridge":
          let field: "colorlow" | "colorVert";
          if (dir === "up" || dir === "down") {
            field = "colorVert";
          } else field = "colorlow";
          this.setState((s) => {
            let st = { ...s };
            st.rev += 1;
            st.elems = [...st.elems];
            st.elems[y][x].selected = true;
            st.elems[sy][sx].selected = false;
            st.sel = [x, y];
            st.elems[y][x][field] = s.sel_col;
            return st;
          });
          break;
        case "empty":
          handlePath(this, dir);
          break;
        case "path": {
          let stash: Direction | undefined;
          if (props.directionA === dir) stash = props.directionB;
          if (props.directionB === dir) stash = props.directionA;
          handlePath(this, dir, stash);
          break;
        }
        case "node": {
          let key: "directionB" | "directionA" = "directionB";
          if (sprops.kind === "path") {
            if (!cellIsFacing(sprops, invertDir(dir))) {
              //not pointing at new cell
              //find fix
              if (sprops.directionB) {
                let dir = sprops.directionB;
                let [ex, ey] = applyDir(dir, [sx, sy]);
                let eprops = this.state.elems[ey][ex];
                if (
                  !cellIsFacing(eprops, dir) ||
                  eprops.colorlow !== sprops.colorlow
                ) {
                  key = "directionA";
                }
              } else {
                let dir = sprops.directionA;
                let [ex, ey] = applyDir(dir, [sx, sy]);
                let eprops = this.state.elems[ey][ex];
                if (
                  !cellIsFacing(eprops, dir) ||
                  eprops.colorlow !== sprops.colorlow
                ) {
                  key = "directionB";
                }
              }
            }
          }
          this.setState((s) => {
            let st = { ...s };
            st.rev += 1;
            st.elems = [...st.elems];
            st.elems[y][x].selected = true;
            st.elems[sy][sx].selected = false;
            st.sel = [x, y];
            st.sel_col = st.elems[y][x].colorlow;
            let sc = st.elems[sy][sx];
            if (CellIsPath(sc) && key) {
              sc[key] = invertDir(dir!);
            }
            return st;
          });
        }
      }
    } else {
      //move selection or select node
      this.setState((s) => {
        let st = { ...s };
        st.rev += 1;
        st.elems = [...st.elems];
        st.elems[y][x].selected = true;
        st.elems[sy][sx].selected = false;
        st.sel = [x, y];
        st.sel_col = st.elems[y][x].colorlow;
        return st;
      });
    }
    this.forceUpdate();
    this.props.logic?.(this.state.elems);

    function handlePath(t: CellGrid, dir: Direction, Bdir?: Direction) {
      switch (sprops.kind) {
        case "path": {
          let key: "directionB" | "directionA" = "directionB";
          if (!cellIsFacing(sprops, invertDir(dir))) {
            //not pointing at new cell
            //find fix
            if (sprops.directionB) {
              let dir = sprops.directionB;
              let [ex, ey] = applyDir(dir, [sx, sy]);
              let eprops = t.state.elems[ey][ex];
              if (
                !cellIsFacing(eprops, dir) ||
                eprops.colorlow !== sprops.colorlow
              ) {
                key = "directionA";
              }
            } else {
              let dir = sprops.directionA;
              let [ex, ey] = applyDir(dir, [sx, sy]);
              let eprops = t.state.elems[ey][ex];
              if (
                !cellIsFacing(eprops, dir) ||
                eprops.colorlow !== sprops.colorlow
              ) {
                key = "directionB";
              }
            }
          }
          t.setState((s) => {
            let st = { ...s };
            st.rev += 1;
            st.elems = [...st.elems];
            st.elems[y][x].selected = true;
            st.elems[sy][sx].selected = false;
            st.sel = [x, y];
            let c = st.elems[y][x];
            c.kind = "path";
            c.colorlow = st.sel_col;
            if (CellIsPath(c)) {
              c.directionA = dir!;
            }
            let sc = st.elems[sy][sx];
            if (CellIsPath(sc) && key) {
              sc[key] = invertDir(dir!);
            }
            return st;
          });
          break;
        }
        case "node":
          t.setState((s) => {
            let st = { ...s };
            st.rev += 1;
            st.elems = [...st.elems];
            st.elems[y][x].selected = true;
            st.elems[sy][sx].selected = false;
            st.sel = [x, y];
            let c = st.elems[y][x];
            c.kind = "path";
            c.colorlow = st.sel_col;
            if (CellIsPath(c)) {
              c.directionA = dir!;
            }
            return st;
          });
          break;
        case "empty":
          t.setState((s) => {
            let st = { ...s };
            st.rev += 1;
            st.elems = [...st.elems];
            st.elems[y][x].selected = true;
            st.elems[sy][sx].selected = false;
            st.sel = [x, y];
            return st;
          });
          break;
        case "bridge":
          t.setState((s) => {
            let st = { ...s };
            st.rev += 1;
            st.elems = [...st.elems];
            st.elems[y][x].selected = true;
            st.elems[sy][sx].selected = false;
            st.sel = [x, y];
            let c = st.elems[y][x];
            c.kind = "path";
            if (CellIsPath(c)) {
              c.directionA = dir!;
              c.directionB = Bdir;
              c.colorlow = GetColorForBridgeElem(sprops, invertDir(dir!))!;
            }
            return st;
          });
      }
    }
  };
}
/**
 * Assumes only one of {@link x} or {@link y} is nonzero
 * @param x x delta
 * @param y y delta
 * @returns direction of delta
 */
function deltaToDir(x: number, y: number): Direction | undefined {
  if (x < -0.1 && Math.abs(y) < 0.1) return "left";
  if (x > 0.1 && Math.abs(y) < 0.1) return "right";
  if (y > 0.1 && Math.abs(x) < 0.1) return "down";
  if (y < -0.1 && Math.abs(x) < 0.1) return "up";
}
function CellIsPath(x: CellProps): x is CellPath {
  return x.kind === "path";
}
function GetColorForBridgeElem(e: CellProps, d: Direction): string | undefined {
  if (e.kind !== "bridge") return;
  if (d === "up" || d === "down") return e.colorVert;
  return e.colorlow;
}
/**
 * 
 * @param x
 * @param y 
 * @param sx 
 * @param sy
 * @returns whether ({@link x},{@link y}) is adjecent to ({@link sx},)
 */
function nextto(x: number, y: number, sx: number, sy: number): boolean {
  if (x + 1 === sx && y === sy) return true;
  if (x - 1 === sx && y === sy) return true;
  if (x === sx && y + 1 === sy) return true;
  if (x === sx && y - 1 === sy) return true;
  return false;
}
